// Phase JB.1 — Gap Report read surface.
//
// Free-to-Paid Conversion Architecture v1.0 §6. Composes Eyebrow,
// Stat, HairlineRow, Pill, Countdown — no new primitives, no token
// edits.
//
// States:
//   - anon:           "Log a slip to see your weekly gap report."
//   - auth, no data:  Countdown to next Monday 08:00 WAT.
//   - auth, populated: week_start, signals_won_missed, foregone_units.
//
// N01: never fabricates numbers. Empty states show no fake stats.
// N04: foregone units are surfaced exactly as the calculator stored
//      them (already capped at 5 units/signal upstream).

import { useEffect, useMemo, type CSSProperties } from "react";
import { Countdown, Eyebrow, HairlineRow, Pill, Stat } from "../super-acca";
import { useAuthSession } from "../../auth/useAuthSession.ts";
import { useGapReport, type GapReport } from "../../conversion/useGapReport.ts";
import { trackEvent } from "../../conversion/trackEvent.ts";

const fontMono = "var(--sa-font-mono)";
const fontBody = "var(--sa-font-body)";

const cardStyle: CSSProperties = {
  border: "1px solid var(--sa-hairline)",
  background: "var(--sa-bg-panel)",
  borderRadius: 8,
  padding: 22,
  margin: "0 auto",
  maxWidth: 1180,
};

const wrapperStyle: CSSProperties = { padding: "12px 48px 24px" };

/** Next Monday 08:00 WAT == 07:00 UTC. */
export function nextGapReportISO(now: Date = new Date()): string {
  const utc = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 7, 0, 0, 0),
  );
  // Days to next Monday: 0=Sun..6=Sat → diff to 1=Mon.
  const dow = utc.getUTCDay();
  let add = (1 - dow + 7) % 7;
  if (add === 0 && utc.getTime() <= now.getTime()) add = 7;
  utc.setUTCDate(utc.getUTCDate() + add);
  return utc.toISOString();
}

export interface GapReportCardProps {
  /** Test override. */
  reportOverride?: GapReport | null;
  /** Test override: force unauth state. */
  forceAnon?: boolean;
}

export function GapReportCard({ reportOverride, forceAnon }: GapReportCardProps = {}): JSX.Element {
  const { user } = useAuthSession();
  const { report: liveReport, loading } = useGapReport();
  const report = reportOverride === undefined ? liveReport : reportOverride;
  const isAnon = forceAnon === true || !user;

  useEffect(() => {
    trackEvent("gap_report_viewed", {
      anon: isAnon,
      has_data: report !== null && report !== undefined,
    });
    // fire once per mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const target = useMemo(() => nextGapReportISO(), []);

  if (isAnon) {
    return (
      <section aria-label="Gap Report" data-testid="gap-report-card-anon" style={wrapperStyle}>
        <div style={cardStyle}>
          <Eyebrow>Gap Report · Weekly</Eyebrow>
          <p
            style={{
              fontFamily: fontBody,
              fontSize: 14,
              color: "var(--sa-fg-secondary)",
              marginTop: 14,
              lineHeight: 1.7,
            }}
          >
            Log a slip to see your weekly gap report. STRIKZA never fabricates numbers — we publish your real ledger or nothing at all.
          </p>
        </div>
      </section>
    );
  }

  if (loading) {
    return (
      <section aria-label="Gap Report" data-testid="gap-report-card-loading" style={wrapperStyle}>
        <div style={cardStyle}>
          <span
            style={{
              fontFamily: fontMono,
              fontSize: 10.5,
              letterSpacing: "0.22em",
              color: "var(--sa-fg-muted)",
            }}
          >
            LOADING YOUR GAP REPORT…
          </span>
        </div>
      </section>
    );
  }

  if (!report) {
    return (
      <section aria-label="Gap Report" data-testid="gap-report-card-empty" style={wrapperStyle}>
        <div style={cardStyle}>
          <Eyebrow>Gap Report · Weekly</Eyebrow>
          <p
            style={{
              fontFamily: fontBody,
              fontSize: 14,
              color: "var(--sa-fg-secondary)",
              marginTop: 14,
              lineHeight: 1.7,
            }}
          >
            Your first gap report drops Monday 08:00 WAT. It will record every signal you locked, every signal you missed, and the unit-foregone for the week — capped at 5 units per signal so the number stays honest.
          </p>
          <div style={{ marginTop: 18, display: "flex", alignItems: "center", gap: 14 }}>
            <span
              style={{
                fontFamily: fontMono,
                fontSize: 10.5,
                letterSpacing: "0.18em",
                color: "var(--sa-fg-muted)",
              }}
            >
              NEXT REPORT IN
            </span>
            <Countdown targetISO={target} compact />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Gap Report" data-testid="gap-report-card-populated" style={wrapperStyle}>
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <Eyebrow>Gap Report · Week of {report.week_start}</Eyebrow>
          <Pill>WEEKLY</Pill>
        </div>
        <div style={{ marginTop: 14 }}>
          <HairlineRow first columns="1fr 1fr">
            <Stat label="Won signals you missed" value={String(report.signals_won_missed)} />
            <Stat
              label="Foregone units"
              value={`${Number(report.foregone_units).toFixed(2)}`}
              accent
            />
          </HairlineRow>
          <HairlineRow columns="1fr 1fr">
            <Stat label="Total signals not locked" value={String(report.signals_missed)} />
            <span
              style={{
                fontFamily: fontMono,
                fontSize: 10,
                letterSpacing: "0.16em",
                color: "var(--sa-fg-muted)",
                justifySelf: "end",
              }}
            >
              CAP 5 UNITS / SIGNAL
            </span>
          </HairlineRow>
        </div>
        <div
          style={{
            marginTop: 14,
            fontFamily: fontBody,
            fontSize: 12.5,
            color: "var(--sa-fg-muted)",
            lineHeight: 1.6,
          }}
        >
          See full breakdown{" "}
          <a
            href="/dashboard"
            style={{ color: "var(--sa-accent-saffron)", textDecoration: "none" }}
            data-testid="gap-report-card-cta"
          >
            on your dashboard →
          </a>
        </div>
      </div>
    </section>
  );
}
